
interface AlertsPanelProps {
  onTooltipShow: (title: string, content: string) => void;
}

export default function AlertsPanel({ onTooltipShow }: AlertsPanelProps) {
  const alerts = [
    {
      type: 'critical',
      title: 'Core Web Vitals Score Dropped',
      message: 'Your Largest Contentful Paint increased to 3.2s, affecting user experience',
      time: '2 hours ago',
      action: 'Optimize Images',
      category: 'performance'
    },
    {
      type: 'success',
      title: 'Keyword Ranking Improved',
      message: '"Digital marketing agency" moved from position #8 to #3',
      time: '4 hours ago',
      action: 'View Details',
      category: 'ranking'
    },
    {
      type: 'warning',
      title: 'Competitor Gained Backlinks',
      message: 'MarketingPro Agency acquired 15 new high-authority backlinks',
      time: '6 hours ago',
      action: 'Analyze Strategy',
      category: 'competition'
    },
    {
      type: 'info',
      title: 'New Content Opportunity',
      message: 'Trending topic "AI in Marketing" shows high search volume',
      time: '8 hours ago',
      action: 'Create Content',
      category: 'content'
    },
    {
      type: 'critical',
      title: 'Crawl Errors Detected',
      message: '23 pages returning 404 errors, affecting indexation',
      time: '12 hours ago',
      action: 'Fix Errors',
      category: 'technical'
    },
    {
      type: 'success',
      title: 'Backlink Acquired',
      message: 'New high-quality backlink from TechCrunch (DA: 92)',
      time: '1 day ago',
      action: 'View Backlink',
      category: 'backlink'
    }
  ];

  const milestones = [
    {
      title: 'Reached 1,000 Keywords in Top 10',
      date: '2024-01-15',
      impact: 'High',
      description: 'Significant milestone in organic visibility'
    },
    {
      title: 'Domain Authority Increased to 67',
      date: '2024-01-10',
      impact: 'Medium',
      description: 'Improved from 62 to 67 in the last quarter'
    },
    {
      title: 'Monthly Traffic Exceeded 50K',
      date: '2024-01-05',
      impact: 'High',
      description: 'First time reaching this traffic milestone'
    }
  ];

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'critical': return 'ri-error-warning-line text-red-500';
      case 'warning': return 'ri-alert-line text-yellow-500';
      case 'success': return 'ri-checkbox-circle-line text-green-500';
      case 'info': return 'ri-information-line text-blue-500';
      default: return 'ri-notification-3-line text-gray-500';
    }
  };

  const getAlertBg = (type: string) => {
    switch (type) {
      case 'critical': return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
      case 'warning': return 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800';
      case 'success': return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800';
      case 'info': return 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800';
      default: return 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'performance': return 'ri-speed-line';
      case 'ranking': return 'ri-arrow-up-line';
      case 'competition': return 'ri-spy-line';
      case 'content': return 'ri-file-text-line';
      case 'technical': return 'ri-settings-3-line';
      case 'backlink': return 'ri-links-line';
      default: return 'ri-notification-3-line';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Alerts & Notifications</h2>
          <button
            onClick={() => onTooltipShow('Alerts & Notifications', 'Real-time monitoring of SEO performance changes, ranking updates, and optimization opportunities')}
            className="text-gray-400 hover:text-blue-500"
          >
            <i className="ri-information-line text-xl"></i>
          </button>
        </div>

        {/* Alert Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
            <div className="text-2xl font-bold text-red-600">2</div>
            <div className="text-sm text-red-700 dark:text-red-400">Critical</div>
          </div>
          <div className="text-center p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
            <div className="text-2xl font-bold text-yellow-600">1</div>
            <div className="text-sm text-yellow-700 dark:text-yellow-400">Warning</div>
          </div>
          <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <div className="text-2xl font-bold text-green-600">2</div>
            <div className="text-sm text-green-700 dark:text-green-400">Success</div>
          </div>
          <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">1</div>
            <div className="text-sm text-blue-700 dark:text-blue-400">Info</div>
          </div>
        </div>
      </div>

      {/* Recent Alerts */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Recent Alerts</h3>
        <div className="space-y-4">
          {alerts.map((alert, index) => (
            <div key={index} className={`p-4 rounded-lg border ${getAlertBg(alert.type)}`}>
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 mt-1">
                  <i className={getAlertIcon(alert.type)}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="font-medium text-gray-900 dark:text-white">{alert.title}</h4>
                    <div className="flex items-center space-x-2">
                      <i className={`${getCategoryIcon(alert.category)} text-gray-400`}></i>
                      <span className="text-xs text-gray-500 dark:text-gray-400">{alert.time}</span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{alert.message}</p>
                  <button className="text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">
                    {alert.action} →
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Performance Anomalies */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Performance Anomalies</h3>
          <div className="space-y-4">
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="flex items-center space-x-3 mb-2">
                <i className="ri-arrow-down-line text-red-500"></i>
                <span className="font-medium text-red-800 dark:text-red-400">Traffic Drop Detected</span>
              </div>
              <p className="text-sm text-red-700 dark:text-red-300">15% decrease in organic traffic over the last 3 days</p>
              <div className="mt-2 text-xs text-red-600 dark:text-red-400">Possible cause: Algorithm update</div>
            </div>
            
            <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <div className="flex items-center space-x-3 mb-2">
                <i className="ri-time-line text-yellow-500"></i>
                <span className="font-medium text-yellow-800 dark:text-yellow-400">Slow Response Time</span>
              </div>
              <p className="text-sm text-yellow-700 dark:text-yellow-300">Average page load time increased to 4.2s</p>
              <div className="mt-2 text-xs text-yellow-600 dark:text-yellow-400">Threshold: 3.0s</div>
            </div>
            
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <div className="flex items-center space-x-3 mb-2">
                <i className="ri-search-line text-blue-500"></i>
                <span className="font-medium text-blue-800 dark:text-blue-400">Indexing Issues</span>
              </div>
              <p className="text-sm text-blue-700 dark:text-blue-300">12 pages not indexed in the last week</p>
              <div className="mt-2 text-xs text-blue-600 dark:text-blue-400">Check robots.txt and sitemaps</div>
            </div>
          </div>
        </div>

        {/* SEO Milestones */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">SEO Milestones</h3>
          <div className="space-y-4">
            {milestones.map((milestone, index) => (
              <div key={index} className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-green-800 dark:text-green-400">{milestone.title}</h4>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    milestone.impact === 'High' ? 'bg-green-200 text-green-800' : 'bg-yellow-200 text-yellow-800'
                  }`}>
                    {milestone.impact} Impact
                  </span>
                </div>
                <p className="text-sm text-green-700 dark:text-green-300 mb-2">{milestone.description}</p>
                <div className="text-xs text-green-600 dark:text-green-400">{milestone.date}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Alert Settings */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Alert Preferences</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { category: 'Ranking Changes', enabled: true, threshold: '±3 positions' },
            { category: 'Traffic Anomalies', enabled: true, threshold: '±20%' },
            { category: 'Core Web Vitals', enabled: true, threshold: 'Any degradation' },
            { category: 'Backlink Changes', enabled: false, threshold: '±10 links' },
            { category: 'Competitor Updates', enabled: true, threshold: 'Major changes' },
            { category: 'Technical Issues', enabled: true, threshold: 'Any errors' }
          ].map((setting, index) => (
            <div key={index} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-gray-900 dark:text-white">{setting.category}</span>
                <div className={`w-10 h-6 rounded-full ${setting.enabled ? 'bg-blue-600' : 'bg-gray-300'} relative transition-colors`}>
                  <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-transform ${setting.enabled ? 'translate-x-5' : 'translate-x-1'}`}></div>
                </div>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Threshold: {setting.threshold}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
